import type { SceneLayoutBlock, SceneLike } from './buildSceneLayout';

interface Placed { from: number; end: number }

/** Index of the block owning `frame`. Inside an overlap (two consecutive
 *  containers) ownership flips to the later block at the overlap midpoint. */
function ownerIndex(placed: Placed[], frame: number): number {
  let first = -1;
  for (let i = 0; i < placed.length; i++) {
    const p = placed[i];
    if (frame >= p.from && frame < p.end) {
      if (first === -1) { first = i; continue; }
      // Second container: consecutive blocks overlap on [p.from, placed[first].end)
      const mid = (p.from + placed[first].end) / 2;
      return frame >= mid ? i : first;
    }
  }
  return first;
}

export function outputFrameToSourceFrame(blocks: SceneLayoutBlock[], frame: number): number {
  if (blocks.length === 0) return 0;
  const placed = blocks.map((b) => ({ from: b.from, end: b.from + b.durationInFrames }));
  const idx = ownerIndex(placed, frame);
  if (idx >= 0) return blocks[idx].trimBefore + (frame - blocks[idx].from);
  if (frame < placed[0].from) return blocks[0].trimBefore;
  // Past the end (or in a zero-length hole): clamp to the last block ending before the frame.
  let last = blocks[0];
  for (let i = 0; i < blocks.length; i++) {
    if (placed[i].end <= frame) last = blocks[i];
  }
  return last.trimAfter;
}

/** SOURCE seconds -> OUTPUT seconds. Mirrors the legacy
 *  absoluteToCompositionTime semantics; only the per-scene output start
 *  changes when overlaps pull scenes forward. */
export function sourceTimeToOutputTime(
  scenes: SceneLike[],
  blocks: SceneLayoutBlock[],
  t: number,
  fps: number,
): number {
  // Array order is playback order, which need not match source timestamps.
  // Find kept footage before considering source gaps.
  for (let i = 0; i < scenes.length && i < blocks.length; i++) {
    if (t >= scenes[i].startTime && t < scenes[i].endTime) {
      return blocks[i].from / fps + t - scenes[i].startTime;
    }
  }
  for (let i = 0; i < scenes.length && i < blocks.length; i++) {
    if (t === scenes[i].endTime) return blocks[i].from / fps + t - scenes[i].startTime;
  }
  let lastEnd = 0;
  for (let i = 0; i < scenes.length && i < blocks.length; i++) {
    const s0 = Math.max(0, scenes[i].startTime);
    const s1 = Math.max(s0, scenes[i].endTime);
    const outStart = blocks[i].from / fps;
    if (t < s0) return outStart;
    if (t <= s1) return outStart + (t - s0);
    lastEnd = outStart + (s1 - s0);
  }
  return lastEnd;
}

export interface MappableInterval {
  start: number;
  end: number;
  finalStartFrame: number;
  finalEndFrame: number;
}

/** OUTPUT frame -> the owning interval's time axis (interval.start-relative).
 *  Replaces the sequential `remaining -= segDuration` walks, which assume
 *  back-to-back placement and drift once overlaps exist. */
export function outputFrameToIntervalTime(
  intervals: MappableInterval[],
  frame: number,
  fps: number,
): number {
  if (intervals.length === 0) return 0;
  const placed = intervals.map((iv) => ({ from: iv.finalStartFrame, end: iv.finalEndFrame }));
  const idx = ownerIndex(placed, frame);
  if (idx >= 0) {
    const iv = intervals[idx];
    return iv.start + (frame - iv.finalStartFrame) / fps;
  }
  if (frame < placed[0].from) return intervals[0].start;
  let last = intervals[0];
  for (let i = 0; i < intervals.length; i++) {
    if (placed[i].end <= frame) last = intervals[i];
  }
  return last.end;
}
